import TYPES from "../../../formComponents/formFields/constants";

import TimeField from "../customFields/timeField/timeField.component";
import RewardPolicyField from "../customFields/rewardPolicyField/rewardPolicyField.component";
import FeedbackTagsFields from "../customFields/feedbackTagsFields/feedbackTagsFields.component";
import ContestPermissionsFields from "../customFields/contestPermissionsFields/contestPermissionsFields.component";
import DeliverableSubmissionFields from "../customFields/deliverableSubmissionFields/deliverableSubmissionFields.component";
import SupplementalSubmissionFields from "../customFields/supplementalSubmissionFields/supplementalSubmissionFields.component";
import OptionGroup from "../customFields/supplementalSubmissionFields/optionGroup.component";

const commonBooleanOptions = [
  {
    label: "Yes",
    value: true,
  },
  {
    label: "No",
    value: false,
  },
];

const contestEvaluationSchemeRubricDefault = {
  criteria_1: {
    name: "1-10 Scale",
    short_name: "1-10 Scale",
    chart_type: "bar_chart",
    color_hex_code: "aa0055",
    color_hex_code_2: "0055aa",
    weight: 1,
    scale: [
      {
        label: "10",
        level: 10,
        min_rating: 9.0,
        max_rating: 10,
        feedback_text: "10",
      },
      {
        label: "9",
        level: 9,
        min_rating: 8.0,
        max_rating: 9.0,
        feedback_text: "9",
      },
      {
        label: "8",
        level: 8,
        min_rating: 7.0,
        max_rating: 8.0,
        feedback_text: "8",
      },
      {
        label: "7",
        level: 7,
        min_rating: 6.0,
        max_rating: 7.0,
        feedback_text: "7",
      },
      {
        label: "6",
        level: 6,
        min_rating: 5.0,
        max_rating: 6.0,
        feedback_text: "6",
      },
      {
        label: "5",
        level: 5,
        min_rating: 4.0,
        max_rating: 5.0,
        feedback_text: "5",
      },
      {
        label: "4",
        level: 4,
        min_rating: 3.0,
        max_rating: 4.0,
        feedback_text: "4",
      },
      {
        label: "3",
        level: 3,
        min_rating: 2.0,
        max_rating: 3.0,
        feedback_text: "3",
      },
      {
        label: "2",
        level: 2,
        min_rating: 1.0,
        max_rating: 2.0,
        feedback_text: "2",
      },
      {
        label: "1",
        level: 1,
        min_rating: 0,
        max_rating: 1.0,
        feedback_text: "1",
      },
    ],
  },
};

const rewardPolicyFields = {
  winner_num: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 0,
  },
  winner_reward: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 1,
  },
  ru_num: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 0,
  },
  ru_reward: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 1,
  },
  hm_num: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 0,
  },
  hm_reward: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 1,
  },
  external_reward_winner_num: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 0,
  },
  external_reward_winner_reward: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    minValue: 1,
  },
  external_reward_info: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    label: "Reward instructions",
  },
  percentile_number: {
    default: undefined,
    value: 50,
    type: TYPES.NUMBER,
    required: true,
    minValue: 1,
    maxValue: 100,
  },
};
const rewardPolicyFieldsWithNames = Object.fromEntries(
  Object.entries(rewardPolicyFields).map(([fKey, fVal]) => [
    fKey,
    { name: fKey, ...fVal },
  ]),
);

const supplementalSubmissionFields = {
  max_characters: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    externalLabel: "Should there be a character limit?",
  },
  placeholder: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    externalLabel: "Placeholder",
  },
  min_value: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    externalLabel: "Min value?",
  },
  max_value: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    externalLabel: "Max value?",
  },
  step_value: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    externalLabel: "Step?",
  },
  correct_answer: {
    default: undefined,
    type: TYPES.SIMPLE_TEXTAREA,
    required: false,
    externalLabel: "Correct answer",
  },
  group_id: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    externalLabel: "Group id",
  },
  order: {
    default: undefined,
    type: TYPES.NUMBER,
    required: false,
    externalLabel: "In what order should this question appear?",
  },
  restricted: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    externalLabel: "Time restriced?",
    options: [...commonBooleanOptions],
    settings: {
      disableOn: [
        { field: "draft_to_submission_time_limit_in_seconds", value: 0 },
      ],
    },
  },
  required: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    externalLabel: "Is this supplemental required?",
    options: [...commonBooleanOptions],
  },
  challenge_filter: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    externalLabel: "Use as challenge filter?",
    options: [...commonBooleanOptions],
  },
  options: {
    default: undefined,
    type: TYPES.SIMPLE_TEXTAREA,
    required: false,
    externalLabel: "Options",
  },
  label: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    externalLabel: "Label",
  },
  option_group: {
    name: "options",
    default: JSON.stringify({ "": "" }),
    CustomComponent: OptionGroup,
    required: false,
  },
};
export const supplementalSubmissionFieldsWithNames = Object.fromEntries(
  Object.entries(supplementalSubmissionFields).map(([fKey, fVal]) => [
    fKey,
    { name: fVal.name || fKey, ...fVal },
  ]),
);

const commonFields = {
  snapshot_pic: {
    default: undefined,
    type: TYPES.IMAGE_FILE,
    required: false,
    acceptExtensions: ["png", "jpg", "jpeg"],
    image_url: "snapshot_pic_url",
  },
  reward_policy: {
    default: undefined,
    type: TYPES.RADIO,
    required: true,
    CustomComponent: RewardPolicyField,
    settings: {
      vertical: true,
    },
    options: [
      {
        label: "Pick a percentage of solvers to earn rewards",
        value: "percentile_",
        settings: {
          fields: {
            percentile_number: rewardPolicyFieldsWithNames.percentile_number,
          },
          pointsSharing: [
            {
              number: 0.2,
              rank_start: 0,
              rank_end: 0.2,
              reward_fraction: 0.625,
            },
            {
              number: 0.3,
              rank_start: 0.2,
              rank_end: 0.5,
              reward_fraction: 0.25,
            },
            {
              number: 0.5,
              rank_start: 0.5,
              rank_end: 1,
              reward_fraction: 0.125,
            },
          ],
        },
      },
      {
        label: "Winners and Honorable Mentions",
        value: "win_hm",
        settings: {
          fields: {
            winner_num: rewardPolicyFieldsWithNames.winner_num,
            winner_reward: rewardPolicyFieldsWithNames.winner_reward,
            hm_num: rewardPolicyFieldsWithNames.hm_num,
            hm_reward: rewardPolicyFieldsWithNames.hm_reward,
          },
        },
      },
      {
        label: "Winners, Runners-Up, and Honorable Mentions",
        value: "win_ru_hm",
        settings: {
          fields: {
            winner_num: rewardPolicyFieldsWithNames.winner_num,
            winner_reward: rewardPolicyFieldsWithNames.winner_reward,
            ru_num: rewardPolicyFieldsWithNames.ru_num,
            ru_reward: rewardPolicyFieldsWithNames.ru_reward,
            hm_num: rewardPolicyFieldsWithNames.hm_num,
            hm_reward: rewardPolicyFieldsWithNames.hm_reward,
          },
        },
      },
      {
        label: "External Reward",
        value: "external_reward",
        settings: {
          fields: {
            external_reward_winner_num:
              rewardPolicyFieldsWithNames.external_reward_winner_num,
            external_reward_winner_reward:
              rewardPolicyFieldsWithNames.external_reward_winner_reward,
            external_reward_info:
              rewardPolicyFieldsWithNames.external_reward_info,
          },
        },
      },
      {
        label: "No Winners/Rewards",
        value: "none",
      },
    ],
  },
  title: {
    default: "",
    type: TYPES.SIMPLE_TEXT,
    required: true,
    label: "Challenge title",
  },
  description: {
    default: "",
    type: TYPES.TEXTAREA,
    required: true,
  },
  company_team_id: {
    default: undefined,
    type: TYPES.SELECT,
    placeholder: "Select Company",
    required: true,
    settings: {
      optionsFetchFrom: {
        url: "/admin/contests/form_selects/company_teams",
        cacheKey: "adminFormSelectsCompanyTeams",
      },
    },
  },
  supplemental_submission_fields: {
    CustomComponent: SupplementalSubmissionFields,
    default: [
      {
        _destroy: false,
        title: "",
        required: false,
        challenge_filter: false,
      },
    ],
    settings: {
      id: {
        name: "id",
        default: undefined,
        required: false,
      },
      _destroy: {
        name: "_destroy",
        default: false,
        required: false,
      },
      title: {
        name: "title",
        default: "",
        type: TYPES.TEXTAREA,
        required: true,
      },
      field_type: {
        name: "field_type",
        default: undefined,
        type: TYPES.SELECT,
        required: true,
        options: [
          {
            label: "check_box",
            value: "check_box",
            fields: [
              supplementalSubmissionFieldsWithNames.option_group,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "input",
            value: "input",
            fields: [
              supplementalSubmissionFieldsWithNames.max_characters,
              supplementalSubmissionFieldsWithNames.placeholder,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "input_other",
            value: "input_other",
            fields: [
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "lookup",
            value: "lookup",
            fields: [
              supplementalSubmissionFieldsWithNames.options,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "number",
            value: "number",
            fields: [
              supplementalSubmissionFieldsWithNames.max_characters,
              supplementalSubmissionFieldsWithNames.placeholder,
              supplementalSubmissionFieldsWithNames.min_value,
              supplementalSubmissionFieldsWithNames.max_value,
              supplementalSubmissionFieldsWithNames.step_value,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "radio",
            value: "radio",
            fields: [
              supplementalSubmissionFieldsWithNames.option_group,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "ranking",
            value: "ranking",
            fields: [
              supplementalSubmissionFieldsWithNames.options,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "select",
            value: "select",
            fields: [
              supplementalSubmissionFieldsWithNames.option_group,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
          {
            label: "textarea",
            value: "textarea",
            fields: [
              supplementalSubmissionFieldsWithNames.max_characters,
              supplementalSubmissionFieldsWithNames.placeholder,
              supplementalSubmissionFieldsWithNames.correct_answer,
              supplementalSubmissionFieldsWithNames.group_id,
              supplementalSubmissionFieldsWithNames.order,
              supplementalSubmissionFieldsWithNames.required,
              supplementalSubmissionFieldsWithNames.challenge_filter,
              supplementalSubmissionFieldsWithNames.restricted,
            ],
          },
        ],
      },
    },
  },
};

const fullScaleCommonFields = {
  ...commonFields,
  snapshot_pic: {
    ...commonFields.snapshot_pic,
    required: true,
  },
  reward_points: {
    default: 16000.0,
    type: TYPES.NUMBER,
    required: true,
  },
  link_to_video: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    label: "Youtube url...",
  },
  short_description: {
    default: "",
    type: TYPES.TEXTAREA,
    required: true,
  },
  generate_ai_solution: {
    default: true,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  privacy_type: {
    default: "public",
    type: TYPES.RADIO,
    required: false,
    options: [
      {
        label: "Everybody",
        value: "public",
      },
      {
        label: "Logged-In Only",
        value: "private",
      },
    ],
  },
  strict_contest_permissions: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  auto_approve_submissions: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  restricted_for_1st_time_solver: {
    default: true,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  no_community_contest: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [
      {
        label: "Yes",
        value: false,
      },
      {
        label: "No",
        value: true,
      },
    ],
  },
  anonymous: {
    default: true,
    type: TYPES.BOOLEAN_RADIO,
    options: [
      {
        label: "Standard",
        value: false,
      },
      {
        label: "Anonymous",
        value: true,
      },
    ],
  },
  require_user_login: {
    default: true,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  multiple_solution_count: {
    default: 1,
    type: TYPES.NUMBER,
    required: true,
    minValue: 1,
    settings: {
      notReadOnlyOn: [
        {
          field: "draft_to_submission_time_limit_in_seconds",
          value: 0,
          setDefault: true,
        },
      ],
    },
  },
  featured: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  quick_solve: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  solutions_private: {
    default: "private",
    type: TYPES.RADIO,
    required: true,
    options: [
      {
        label: "Private",
        value: "private",
      },
      {
        label: "Public",
        value: "public",
      },
    ],
  },
  url_title: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
  },
  hide_entities: {
    default: undefined,
    type: TYPES.SIMPLE_TEXTAREA,
    required: false,
  },
  rep_email: {
    default: undefined,
    type: TYPES.SELECT,
    placeholder: "Select Rep",
    required: true,
    settings: {
      optionsFetchFrom: {
        url: "/admin/contests/form_selects/mindsumo_team",
        cacheKey: "adminFormSelectsMindSumoTeam",
      },
    },
  },
  manager_email: {
    default: undefined,
    type: TYPES.SELECT,
    placeholder: "Select Manager",
    required: true,
    settings: {
      optionsFetchFrom: {
        url: "/admin/contests/form_selects/mindsumo_team",
        cacheKey: "adminFormSelectsMindSumoTeam",
      },
    },
  },
  contact_name: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    label: "Contact name",
    required: true,
  },
  contact_email: {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    label: "Contact Email",
    required: true,
  },
  "contest_evaluation_scheme.evaluation_rubric": {
    default: JSON.stringify(contestEvaluationSchemeRubricDefault),
    type: TYPES.JSON,
    required: true,
    label: "Evaluation Scheme",
  },
  "contest_evaluation_scheme.pairwise_rating_scheme": {
    default: "none",
    type: TYPES.RADIO,
    externalLabel: "Pairwise Rating",
    required: true,
    options: [
      {
        label: "None",
        value: "none",
      },
      {
        label: "Simple",
        value: "simple",
      },
    ],
  },
  feedback_tags: {
    CustomComponent: FeedbackTagsFields,
    default: [
      {
        name: "Well-written",
        _destroy: false,
      },
      {
        name: "Poorly Written - grammar",
        _destroy: false,
      },
      {
        name: "Poorly written - spelling",
        _destroy: false,
      },
      {
        name: "Poorly written - not clear",
        _destroy: false,
      },
      {
        name: "Unique idea",
        _destroy: false,
      },
      {
        name: "Unoriginal idea",
        _destroy: false,
      },
      {
        name: "Followed the prompt",
        _destroy: false,
      },
      {
        name: "Missing requirements/did not follow the prompt",
        _destroy: false,
      },
    ],
    settings: {
      name: {
        name: "name",
        default: "",
        type: TYPES.SIMPLE_TEXT,
        required: false,
        label: "Feedback tag...",
      },
      id: {
        name: "id",
        default: undefined,
        required: false,
      },
      _destroy: {
        name: "_destroy",
        default: false,
        required: false,
      },
    },
  },
  "target_problem_solvers.criteria": {
    default: "everybody",
    type: TYPES.RADIO,
    required: false,
    options: [
      {
        label: "Everybody",
        value: "everybody",
      },
      {
        label: "Top-Tier Only",
        value: "top_tier",
      },
      {
        label: "1st-Tier & 2nd-Tier",
        value: "first_and_second_tier",
      },
    ],
  },
  custom_banner_color_helper: {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    externalLabel: "Do you want to customize the banner's color?",
    useInForm: false,
    options: [...commonBooleanOptions],
  },
  "contest_layout_customization.custom_banner_color": {
    default: undefined,
    type: TYPES.COLOR,
    required: false,
    settings: {
      disableOn: [{ field: "custom_banner_color_helper", value: false }],
    },
  },
  "contest_layout_customization.custom_banner_logo": {
    default: undefined,
    type: TYPES.IMAGE_FILE,
    required: false,
    acceptExtensions: ["png", "jpg", "jpeg"],
    image_url: "custom_banner_logo_url",
  },
  "contest_layout_customization.custom_banner_text": {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
  },
  "contest_layout_customization.hide_header_layout": {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  "contest_layout_customization.hide_footer_layout": {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  "contest_layout_customization.attach_file_to_submission": {
    default: true,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  "contest_layout_customization.attach_file_to_submission_label": {
    default: undefined,
    type: TYPES.SIMPLE_TEXT,
    required: false,
    label: 'Label what files to be attached (eg. "Latest resume")',
  },
  "contest_layout_customization.remove_custom_banner_logo": {
    default: false,
    type: TYPES.CHECKBOX,
    required: false,
    options: [
      {
        label: "Remove any custom banner logo",
        value: true,
      },
    ],
  },
  "contest_layout_customization.hide_demographics_section_on_dashboard": {
    default: false,
    type: TYPES.BOOLEAN_RADIO,
    options: [...commonBooleanOptions],
  },
  contest_permissions: {
    CustomComponent: ContestPermissionsFields,
    default: [
      {
        _destroy: false,
        category: "domain",
        permission_type: "inclusion",
        criteria: undefined,
      },
    ],
    settings: {
      id: {
        name: "id",
        default: undefined,
        required: false,
      },
      _destroy: {
        name: "_destroy",
        default: false,
        required: false,
      },
      category: {
        name: "category",
        default: "domain",
        type: TYPES.SELECT,
        required: false,
        options: [
          {
            label: "domain",
            value: "domain",
          },
          {
            label: "location",
            value: "location",
          },
        ],
      },
      permission_type: {
        name: "permission_type",
        default: "inclusion",
        type: TYPES.SELECT,
        required: false,
        options: [
          {
            label: "inclusion",
            value: "inclusion",
          },
          {
            label: "exclusion",
            value: "exclusion",
          },
        ],
      },
      criteria: {
        default: undefined,
        name: "criteria",
        required: false,
        domain: {
          type: TYPES.SIMPLE_TEXT,
          label: "Criteria...",
        },
        location: {
          type: TYPES.SELECT,
          settings: {
            optionsFetchFrom: {
              url: "/admin/contests/form_selects/contest_permission_locations",
              cacheKey: "adminFormSelectsContestPermissionLocations",
            },
          },
        },
      },
    },
  },
  contestfiles: {
    type: TYPES.FILES,
    settings: {
      signedUrl: "/admin/contestfiles/signed_url",
      deleteCallbackUrl: "/admin/contestfiles/deleted_callback",
      uploadedCallbackUrl: "/admin/contestfiles/uploaded_callback",
    },
  },
};

const inputsConfig = {
  ideation: {
    ...commonFields,
    reward_policy: {
      default: "crowd_voting",
      type: TYPES.RADIO,
      required: true,
      settings: {
        vertical: true,
      },
      options: [
        {
          label: "Crows Vating",
          value: "crowd_voting",
        },
        {
          label: "Winners and Honorable Mentions",
          value: "win_hm",
        },
        {
          label: "No Winners/Rewards",
          value: "none",
        },
      ],
    },
  },
  timed: {
    ...fullScaleCommonFields,
    deadline: {
      default: undefined,
      type: TYPES.DATETIME,
      required: true,
    },
    draft_to_submission_time_limit_in_seconds: {
      default: 0,
      type: TYPES.NUMBER,
      required: true,
      minValue: 0,
    },
    max_bibliographies_count: {
      default: 3,
      type: TYPES.NUMBER,
      required: true,
      minValue: 0,
    },
    cap_enabled: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    cap_limit: {
      default: 300,
      type: TYPES.NUMBER,
      required: true,
      label: "SPOTS",
      minValue: 0,
    },
    cap_submit_time_limit_in_seconds: {
      default: 43200,
      required: false,
      CustomComponent: TimeField,
      minValue: 0,
    },
    approved_cap_enabled: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    approved_cap_limit: {
      default: 150,
      type: TYPES.NUMBER,
      required: true,
      label: "SPOTS",
      minValue: 0,
    },
    show_criteria: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    comments_visible_to_challengers: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      externalLabel: "Should comments be visible to clients?",
      options: [...commonBooleanOptions],
    },
    comments_require_approvals: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      externalLabel: "Should admin approvals be required for comments?",
      options: [...commonBooleanOptions],
    },
    contest_goal: {
      default: "innovation",
      type: TYPES.RADIO,
      required: true,
      options: [
        {
          label: "Innovation",
          value: "innovation",
        },
        {
          label: "Insights",
          value: "insights",
        },
      ],
    },
    show_in_upcoming: {
      default: false,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    expected_launch_date: {
      default: undefined,
      type: TYPES.DATETIME,
      required: true,
      label: "Expected launch date",
      settings: {
        disableOn: [{ field: "show_in_upcoming", value: false }],
      },
    },
    deliverable_submission_fields: {
      CustomComponent: DeliverableSubmissionFields,
      default: [
        {
          _destroy: false,
          title: "",
          required: "false",
        },
      ],
      settings: {
        id: {
          name: "id",
          default: undefined,
          required: false,
        },
        _destroy: {
          name: "_destroy",
          default: false,
          required: false,
        },
        title: {
          name: "title",
          default: "",
          type: TYPES.TEXTAREA,
          required: true,
        },
        max_characters: {
          name: "max_characters",
          default: undefined,
          type: TYPES.NUMBER,
          required: false,
          externalLabel: "Should there be a character limit?",
        },
        order: {
          name: "order",
          default: undefined,
          type: TYPES.NUMBER,
          required: false,
          externalLabel: "In what order should this question appear?",
        },
        required: {
          name: "required",
          default: false,
          type: TYPES.BOOLEAN_RADIO,
          externalLabel: "Is this deliverable required?",
          options: [...commonBooleanOptions],
        },
      },
    },
  },
  submission: {
    ...fullScaleCommonFields,
    requested_solutions: {
      default: 20,
      type: TYPES.NUMBER,
      required: true,
      minValue: 20,
      maxValue: 60,
    },
    draft_to_submission_time_limit_in_seconds: {
      default: 0,
      type: TYPES.NUMBER,
      required: true,
      minValue: 0,
    },
    max_bibliographies_count: {
      default: 3,
      type: TYPES.NUMBER,
      required: true,
      minValue: 0,
    },
    show_criteria: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    comments_visible_to_challengers: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      externalLabel: "Should comments be visible to clients?",
      options: [...commonBooleanOptions],
    },
    comments_require_approvals: {
      default: true,
      type: TYPES.BOOLEAN_RADIO,
      externalLabel: "Should admin approvals be required for comments?",
      options: [...commonBooleanOptions],
    },
    contest_goal: {
      default: "innovation",
      type: TYPES.RADIO,
      required: true,
      options: [
        {
          label: "Innovation",
          value: "innovation",
        },
        {
          label: "Insights",
          value: "insights",
        },
      ],
    },
    show_in_upcoming: {
      default: false,
      type: TYPES.BOOLEAN_RADIO,
      options: [...commonBooleanOptions],
    },
    expected_launch_date: {
      default: undefined,
      type: TYPES.DATETIME,
      required: true,
      label: "Expected launch date",
      settings: {
        disableOn: [{ field: "show_in_upcoming", value: false }],
      },
    },
    deliverable_submission_fields: {
      CustomComponent: DeliverableSubmissionFields,
      default: [
        {
          _destroy: false,
          title: "",
          required: "false",
        },
      ],
      settings: {
        id: {
          name: "id",
          default: undefined,
          required: false,
        },
        _destroy: {
          name: "_destroy",
          default: false,
          required: false,
        },
        title: {
          name: "title",
          default: "",
          type: TYPES.TEXTAREA,
          required: true,
        },
        max_characters: {
          name: "max_characters",
          default: undefined,
          type: TYPES.NUMBER,
          required: false,
          externalLabel: "Should there be a character limit? (optional)",
        },
        order: {
          name: "order",
          default: undefined,
          type: TYPES.NUMBER,
          required: false,
          externalLabel:
            "In what order should this question appear? (optional)",
        },
        required: {
          name: "required",
          default: false,
          type: TYPES.BOOLEAN_RADIO,
          externalLabel: "Is this deliverable required?",
          options: [...commonBooleanOptions],
        },
      },
    },
  },
  questionnaire: {
    ...fullScaleCommonFields,
    no_community_contest: {
      ...fullScaleCommonFields.no_community_contest,
      default: true,
    },
    auto_approve_submissions: {
      ...fullScaleCommonFields.auto_approve_submissions,
      default: true,
    },
    restricted_for_1st_time_solver: {
      ...fullScaleCommonFields.restricted_for_1st_time_solver,
      default: false,
    },
    require_user_login: {
      ...fullScaleCommonFields.require_user_login,
      default: false,
    },
    deadline: {
      default: undefined,
      type: TYPES.DATETIME,
      required: true,
    },
  },
};

const inputsConfigWithNames = Object.fromEntries(
  Object.entries(inputsConfig).map(([key, value]) => {
    const val = Object.fromEntries(
      Object.entries(value).map(([fKey, fVal]) => [
        fKey,
        { name: fKey, ...fVal },
      ]),
    );
    return [key, val];
  }),
);
export default inputsConfigWithNames;

export const contestTypeOptions = [
  {
    label: "Timed",
    value: "timed",
  },
  {
    label: "Ideation",
    value: "ideation",
  },
  {
    label: "Submission",
    value: "submission",
  },
  {
    label: "Questionnaire",
    value: "questionnaire",
  },
];
